 import Axios from 'axios-lib';

// Lazily initialize Axios instance to avoid TDZ/circular import issues
let client;
function initClient() {
  if (client) return client;
  client = Axios.create({
    timeout: 15000,
    withCredentials: true,
    headers: {
      // Do NOT force Content-Type globally; let Axios set it per request.
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  });
  // Add request interceptor for CSRF token
  client.interceptors.request.use((config) => {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (token) {
      config.headers['X-CSRF-TOKEN'] = token;
    }
    // Also try Laravel's default XSRF cookie header for robustness
    try {
      const cookie = document.cookie.split('; ').find(c => c.startsWith('XSRF-TOKEN='));
      if (cookie) {
        const xsrf = decodeURIComponent(cookie.split('=')[1]);
        if (xsrf) config.headers['X-XSRF-TOKEN'] = xsrf;
      }
    } catch (_) {}
    // If sending FormData, ensure we don't override multipart boundary
    if (config.data instanceof FormData) {
      try { delete config.headers['Content-Type']; } catch (_) {}
    }
    return config;
  });
  // Add response interceptor for common error handling
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      const errorStatus = error.response?.status;
      // Don't log expected warnings (like "no entries found")
      const isExpectedWarning = errorMessage.includes('Aucune écriture');
      if (errorMessage !== 'Unknown error' && errorStatus >= 400 && errorStatus !== 422 && !isExpectedWarning) {
        console.error(`🔴 API Error [${errorStatus}]:`, errorMessage);
      }
      return Promise.reject(error);
    }
  );
  return client;
}

// Proxy that defers access until the first usage
function api(...args) {
  const c = initClient();
  return c(...args);
}
const apiProxy = new Proxy(api, {
  get(_target, prop) {
    const c = initClient();
    const value = c[prop];
    return typeof value === 'function' ? value.bind(c) : value;
  },
  set(_target, prop, value) {
    const c = initClient();
    c[prop] = value;
    return true;
  },
  apply(_target, thisArg, args) {
    const c = initClient();
    return c.apply(thisArg, args);
  }
});

// Expose axios static helpers so libraries (e.g., Inertia) that import this
// module via the 'axios' alias and expect axios.* helpers continue to work.
// These properties are read from the real axios export (aliased here as 'axios-lib').
apiProxy.isCancel = Axios.isCancel;
apiProxy.isAxiosError = Axios.isAxiosError;
// Backward-compat helpers (may be deprecated but harmless to expose)
apiProxy.CancelToken = Axios.CancelToken;
apiProxy.all = Axios.all;
apiProxy.spread = Axios.spread;

export default apiProxy;
